import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Phone, RefreshCw, Users } from "lucide-react";
import { API, INSTAGRAM_URL } from "../config";
import { LogoMark } from "../components/Logo";

const EASE = [0.16, 1, 0.3, 1];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const waHref = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  const withCC = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${withCC}`;
};

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErro(false);
    try {
      const res = await fetch(`${API}/leads`);
      const data = await res.json();
      setLeads(Array.isArray(data) ? data : []);
    } catch {
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mesAtual = new Date().toLocaleString("pt-BR", { month: "long" });
  const leadsMes = leads.filter((l) => {
    try {
      return new Date(l.created_at).getMonth() === new Date().getMonth();
    } catch {
      return false;
    }
  }).length;

  return (
    <div className="min-h-screen bg-background font-body text-foreground">
      <header className="border-b border-white/10">
        <div className="container-x flex h-[72px] items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center bg-white text-black">
              <LogoMark className="h-5 w-5" />
            </span>
            <div className="leading-none">
              <p className="font-logo text-sm uppercase tracking-[0.3em] text-white">
                Painel de Leads
              </p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">
                LF Team — Consultoria Premium
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="leads-refresh"
              onClick={load}
              className="grid h-10 w-10 place-items-center border border-white/20 text-white transition-colors hover:bg-white hover:text-black"
              aria-label="Atualizar"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-white sm:block"
            >
              @lucasfucht
            </a>
          </div>
        </div>
      </header>

      <main className="container-x py-10 md:py-14">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white md:text-5xl">
              Alunos que{" "}
              <span className="text-stroke">responderam.</span>
            </h1>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
              Quiz completo — do mais recente ao mais antigo
            </p>
          </div>
          <div className="flex gap-3">
            <div className="border border-white/10 bg-card px-6 py-4">
              <p className="font-display text-3xl font-black text-white" data-testid="leads-total">
                {loading ? "—" : leads.length}
              </p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
                Total de leads
              </p>
            </div>
            <div className="border border-white/10 bg-card px-6 py-4">
              <p className="font-display text-3xl font-black text-white" data-testid="leads-month">
                {loading ? "—" : leadsMes}
              </p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">
                Em {mesAtual}
              </p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mt-20 flex items-center justify-center gap-3 text-white/60">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando leads...
          </div>
        )}

        {!loading && erro && (
          <div className="mt-14 border border-white/10 bg-card p-10 text-center">
            <p className="text-white/70">Não foi possível carregar os leads.</p>
            <button onClick={load} className="btn-primary mt-6">
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !erro && leads.length === 0 && (
          <div className="mt-14 border border-white/10 bg-card p-12 text-center" data-testid="leads-empty">
            <Users className="mx-auto h-8 w-8 text-white/30" />
            <p className="mt-4 font-display text-2xl font-bold uppercase text-white">
              Nenhum lead ainda
            </p>
            <p className="mt-2 text-sm text-white/50">
              Assim que alguém responder o quiz no site, ele aparece aqui.
            </p>
          </div>
        )}

        {!loading && !erro && leads.length > 0 && (
          <div className="mt-10 flex flex-col gap-4">
            {leads.map((lead, i) => (
              <motion.article
                key={lead.id || i}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4), ease: EASE }}
                data-testid="lead-card"
                className="border border-white/10 bg-card p-6 md:p-8"
              >
                <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="font-display text-2xl font-black uppercase tracking-tight text-white">
                      {lead.name}
                    </p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                      {fmtDate(lead.created_at)}
                    </p>
                    {lead.goal && (
                      <p className="mt-3 inline-block border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/70">
                        {lead.goal}
                      </p>
                    )}
                  </div>
                  <a
                    href={waHref(lead.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lead-whatsapp"
                    className="inline-flex h-12 shrink-0 items-center justify-center gap-2.5 bg-white px-5 font-display text-base font-bold uppercase tracking-wide text-black transition-colors hover:bg-neutral-200"
                  >
                    <Phone className="h-4 w-4" />
                    {lead.phone}
                  </a>
                </div>
                {lead.message && (
                  <div className="mt-5 border-t border-white/10 pt-5">
                    <p className="whitespace-pre-line text-sm leading-relaxed text-white/70 md:text-base">
                      {lead.message}
                    </p>
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-white/10 py-6">
        <p className="container-x font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          © {new Date().getFullYear()} LF Team — Painel interno
        </p>
      </footer>
    </div>
  );
}
